import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import InfiniteScroll from "react-infinite-scroll-component";
import moment, { Moment } from "moment-timezone";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import DataTable, { DataTableColumn } from "components/common/DataTable";
import DateTimeFilter from "components/common/DateTimeFilter";
import { getSelfCheckErrorLogs } from "service/gatewayService";
import {
    GatewayLogEntry,
    GatewayLogType,
    JournalEntry,
} from "types/Gateways";
import { isHttpSuccess } from "utils/functions";
import { getAPIError, showErrorAlert } from "utils/alert";
import { dateTimeFormatter } from "utils/dateTimeFormatter";
import { HttpStatus } from "constant";
import { canAccess } from "utils/authorize-action";

/**
 * Operation code to human-readable name mapping
 */
const OPERATION_NAME_MAP: Record<string, string> = {
    end_ota: "OTA Status",
    lds_port_pwr_status: "Port Power Status",
    rtc_status: "RTC Status",
    ldsu_status: "LDSU Status",
    system_status: "System Status",
    ldsu_port_status: "LDSU Port Status",
};

/**
 * Get human-readable operation name from operation code
 */
const getOperationName = (operation: string): string => {
    return OPERATION_NAME_MAP[operation] || operation;
};

/**
 * Derive log type from journal entry attributes.level
 * Default to Info if level is not present
 */
const deriveLogType = (level?: string): GatewayLogType => {
    if (level === "Error") {
        return GatewayLogType.Error;
    }
    return GatewayLogType.Info;
};

/**
 * Extract description from gateway_message attribute
 * gateway_message can be a string or an object
 */
const getDescription = (gatewayMessage?: string | Record<string, unknown>): string => {
    if (!gatewayMessage) {
        return "-";
    }
    if (typeof gatewayMessage === "string") {
        return gatewayMessage || "-";
    }
    // If it's an object, stringify it
    try {
        return JSON.stringify(gatewayMessage);
    } catch {
        return "-";
    }
};

/**
 * Transform journal entry to GatewayLogEntry format
 */
const transformJournalEntry = (entry: JournalEntry): GatewayLogEntry => ({
    uuid: entry.uuid,
    event_name: getOperationName(entry.operation),
    description: getDescription(entry.attributes?.gateway_message),
    timestamp: entry.create_time,
    type: deriveLogType(entry.attributes?.level),
});

const getLocalTimestamp = (dateTime: string) => moment.utc(dateTime).local();
const toUtcIsoString = (date: Date) => moment(date).utc().toISOString();

const columns: DataTableColumn<GatewayLogEntry>[] = [
    {
        label: "Event Name",
        key: "event_name",
        colProps: { sm: 3 },
        render: (log: GatewayLogEntry) => log.event_name,
    },
    {
        label: "Description",
        key: "description",
        colProps: { sm: 4 },
        render: (log: GatewayLogEntry) => log.description,
    },
    {
        label: "Timestamp",
        key: "timestamp",
        colProps: { sm: 3 },
        render: (log: GatewayLogEntry) => {
            const localTimestamp = getLocalTimestamp(log.timestamp);
            const date = dateTimeFormatter.date(localTimestamp);
            const time = dateTimeFormatter.time(localTimestamp);
            return `${date} ${time}`;
        },
    },
    {
        label: "Type",
        key: "type",
        colProps: { sm: 2 },
        render: (log: GatewayLogEntry) => (
            <span>{log.type}</span>
        ),
    },
];

const CHIP_SX = {
    backgroundColor: "#00cbaf",
    color: "black",
} as const;

const MAX_LOAD_DAYS = 10;
const LOGS_PER_PAGE = 50;
const GATEWAY_LOG_SCROLL_TARGET_ID = "gatewayLogScrollList";

const GatewayLogPage = () => {
    const { gatewayId }: { gatewayId: string } = useParams();
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isForbidden, setIsForbidden] = useState(false);
    const [allLogEntries, setAllLogEntries] = useState<GatewayLogEntry[]>([]);

    // Track the oldest fetched start date for load more
    const [oldestFetchedStart, setOldestFetchedStart] = useState<Date>(
        moment().subtract(MAX_LOAD_DAYS, "days").add(10, "seconds").toDate()
    );
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [isFilterData, setIsFilterData] = useState(false);
    const [filterStart, setFilterStart] = useState<Date | null>(null);
    const [filterEnd, setFilterEnd] = useState<Date | null>(null);
    const [dateFilterMessage, setDateFilterMessage] = useState("");
    const [visibleCount, setVisibleCount] = useState(LOGS_PER_PAGE);
    const prevLogLengthRef = useRef(0);

    //List gets smaller(e.g.filter returns fewer results) → reset back to 50 rows
    useEffect(() => {
        if (allLogEntries.length < prevLogLengthRef.current) {
            setVisibleCount(LOGS_PER_PAGE);
        }

        prevLogLengthRef.current = allLogEntries.length;
    }, [allLogEntries.length]);

    // Filter / clear / switch gateway → reset back to 50 rows
    useEffect(() => {
        setVisibleCount(LOGS_PER_PAGE);
    }, [filterStart, filterEnd, gatewayId]);

    const visibleLogEntries = allLogEntries.slice(0, visibleCount);
    const hasMoreLogs = visibleCount < allLogEntries.length;

    // Increases visibleCount by 50 when the user scrolls to the bottom
    const loadMoreLogs = useCallback(() => {
        setVisibleCount((prev) =>
            Math.min(prev + LOGS_PER_PAGE, allLogEntries.length)
        );
    }, [allLogEntries.length]);

    const fetchLogEntries = useCallback(
        async (start: Date, end: Date, append = false) => {
            try {
                if (append) {
                    setIsLoadingMore(true);
                } else {
                    setIsPageLoading(true);
                }
                const response = await getSelfCheckErrorLogs(gatewayId, {
                    start: toUtcIsoString(start),
                    end: toUtcIsoString(end),
                });

                if (response.status === HttpStatus.FORBIDDEN) {
                    setIsForbidden(true);
                    return;
                }

                if (isHttpSuccess(response.status)) {
                    const journalEntries: JournalEntry[] =
                        response.data?.data ?? response.data ?? [];

                    // Transform journal entries to display format
                    const logs = journalEntries.map(transformJournalEntry);
                    if (append) {
                        setAllLogEntries((prev) => [...prev, ...logs]);
                        setVisibleCount((prev) => prev + logs.length);
                    } else {
                        setAllLogEntries(logs);
                    }
                } else {
                    showErrorAlert({
                        message: getAPIError(response, "Failed to fetch gateway logs").message,
                    });
                }
            } catch (error) {
                showErrorAlert({
                    message:
                        (error as Error).message ?? "Failed to fetch gateway logs",
                });
            } finally {
                setIsPageLoading(false);
                setIsLoadingMore(false);
            }
        },
        [gatewayId]
    );

    useEffect(() => {
        if (!canAccess("gateway:read")) {
            setIsForbidden(true);
            setIsPageLoading(false);
            return;
        }

        const initialStart = moment().subtract(MAX_LOAD_DAYS, "days").add(10, "seconds").toDate();
        const initialEnd = new Date();
        setOldestFetchedStart(initialStart);
        fetchLogEntries(initialStart, initialEnd);
    }, [gatewayId, fetchLogEntries]);

    const handleFilter = useCallback(
        (start: Moment, end: Moment) => {
            const startDate = start.toDate();
            const endDate = end.toDate();
            setFilterStart(startDate);
            setFilterEnd(endDate);
            setIsFilterData(true);
            fetchLogEntries(startDate, endDate);
        },
        [fetchLogEntries]
    );

    const handleClearFilter = useCallback(() => {
        setIsFilterData(false);
        setFilterStart(null);
        setFilterEnd(null);
        const initialStart = moment().subtract(MAX_LOAD_DAYS, "days").add(10, "seconds").toDate();
        const initialEnd = new Date();
        setOldestFetchedStart(initialStart);
        fetchLogEntries(initialStart, initialEnd);
    }, [fetchLogEntries]);

    // Load more: fetch the next 10-day chunk going backwards
    const handleLoadMore = useCallback(() => {
        const newEnd = moment(oldestFetchedStart).toDate();
        const newStart = moment(oldestFetchedStart).subtract(MAX_LOAD_DAYS, "days").toDate();
        setOldestFetchedStart(newStart);
        fetchLogEntries(newStart, newEnd, true);
    }, [oldestFetchedStart, fetchLogEntries]);

    return (
        <ContentWrapper
            isLoading={isPageLoading}
            isForbiddenResource={isForbidden}
        >
            <div className="page-content">
                <Container fluid>
                    <Row className="mb-5">
                        <Col className="d-flex flex-column">
                            <h5 className="page-title mb-1">Gateway Logs</h5>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col>
                            <DateTimeFilter
                                onFilter={handleFilter}
                                onValidationMessageChange={setDateFilterMessage}
                            />
                            {dateFilterMessage ? (
                                <p
                                    className="text-danger mt-1 mb-0"
                                    role="alert"
                                >
                                    {dateFilterMessage}
                                </p>
                            ) : null}
                        </Col>
                    </Row>

                    {isFilterData && filterStart && filterEnd && (
                        <Row className="mb-2">
                            <Col className="d-flex align-items-center">
                                <Typography variant="body1">Filtered Data :</Typography>
                                <Chip
                                    className="ml-2"
                                    sx={CHIP_SX}
                                    size="medium"
                                    label={`Start Date: ${dateTimeFormatter.date(
                                        filterStart
                                    )} - ${dateTimeFormatter.time(
                                        filterStart
                                    )} - End Date: ${dateTimeFormatter.date(
                                        filterEnd
                                    )} - ${dateTimeFormatter.time(filterEnd)}`}
                                    variant="outlined"
                                    onDelete={handleClearFilter}
                                />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col>
                            {allLogEntries.length === 0 ? (
                                <DataTable
                                    columns={columns}
                                    data={allLogEntries}
                                    rowKey={(row) => row.uuid}
                                    emptyContent="No logs found"
                                    className="cstm-table self-check-error-log-table"
                                />
                            ) : (
                                <div
                                    id={GATEWAY_LOG_SCROLL_TARGET_ID}
                                    style={{
                                        maxHeight: "550px",
                                        overflowY: "auto",
                                    }}
                                >
                                    <InfiniteScroll
                                        dataLength={visibleLogEntries.length}
                                        next={loadMoreLogs}
                                        hasMore={hasMoreLogs}
                                        scrollableTarget={
                                            GATEWAY_LOG_SCROLL_TARGET_ID
                                        }
                                        loader={
                                            <div className="text-center py-3">
                                                Loading more logs...
                                            </div>
                                        }
                                    >
                                        <DataTable
                                            columns={columns}
                                            data={visibleLogEntries}
                                            rowKey={(row) => row.uuid}
                                            emptyContent="No logs found"
                                            className="cstm-table self-check-error-log-table"
                                        />
                                    </InfiniteScroll>
                                </div>
                            )}
                        </Col>
                    </Row>

                    {!isFilterData && (
                        <Row className="mt-3">
                            <Col className="text-center">
                                <Button onClick={handleLoadMore} disabled={isLoadingMore}>
                                    {isLoadingMore ? "Loading..." : "Load More"}
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default GatewayLogPage;
